import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React from 'react';

import ManageCookiesForm from '../components/forms/ManageCookiesForm';
import HeaderSection from '../components/layout/sections/HeaderSection';
import PageSection from '../components/layout/sections/PageSection';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import GasparCookiesProvider from '../providers/GasparCookiesProvider/GasparCookiesProvider';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';

const TabPanels = dynamic(() => import('../components/interactive/TabPanels'), { ssr: false });

type CookiesPolicyPageStaticProps = {};

export const getStaticProps: GetStaticProps<CookiesPolicyPageStaticProps> = async (context) => {
  const props: CookiesPolicyPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const CookiesPolicyPage: NextPageWithLayout<CookiesPolicyPageStaticProps> = (_props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.CookiesPolicy);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        title="Cookies Policy"
        description={
          <p>
            Gaspar AI respects the privacy of visitors to its website (as defined in our
            <Link target="_blank" rel="nofollow" href={pageRoutes.PrivacyPolicy}>
              Privacy Policy
            </Link>
            ). Our Cookies Policy applies to the Gaspar AI website and describes the information we collect by automated means through the
            use of information gathering tools, such as cookies.
          </p>
        }
      />
      <PageSection>
        <h2 className="text-section-title">In Depth</h2>
        <p>
          You can see the
          <Link target="_blank" rel="nofollow" href={pageRoutes.PrivacyPolicy}>
            Privacy Policy
          </Link>
          to learn how we protect your privacy in our use of cookies and other information.
        </p>
        <TabPanels
          panelColumnMinWidth={400}
          panels={[
            {
              tabId: 'what-are-cookies',
              tabLabel: 'What are cookies',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>What are cookies</h3>
                  <p>
                    Cookies are small pieces of text sent to your browser by a website that you visit, used to store or track information
                    about your use of the website. They help that website remember information about your visit, which can both make it
                    easier to visit the site again and make the site more useful to you. Other technologies, including unique identifiers
                    used to identify a browser, app or device, pixels and local storage, can also be used for these purposes.
                  </p>
                  <p>
                    Cookies come in many forms. We have set out below the main types of cookies and categories/reasons why they are used.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'types-of-cookies',
              tabLabel: 'Types of cookies',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>Types of cookies</h3>
                  <p>This section refers to all types of cookies and not just those we use on our website. </p>
                  <p>
                    Cookies can be first-party or third-party. The difference between them is based on the domain that sets the cookie. When
                    a user visits a website, the cookies set by that website are known as first-party cookies. These cookies are placed by
                    the same domain that the user is currently visiting and are used to remember user preferences and enhance their browsing
                    experience.
                  </p>
                  <p>
                    Third-party cookies are set by domains that are different from the website the user is currently visiting. These cookies
                    are often placed by third-party services such as advertisers, social media platforms, or analytics providers. They are
                    used to track user behavior across different websites and gather data for targeted advertising, website analytics, or
                    other purposes.
                  </p>
                  <p>
                    Cookies can also be persistent or session-based. Persistent cookies, as the name suggests, remain on a user&#39;s device
                    for a specified period, which is determined by the website that created the cookie. They are stored on the user&#39;s
                    device and are activated every time the user visits the website that placed the cookie. Persistent cookies are used for
                    various purposes, such as remembering login details or user preferences, and can be used to personalize a user&#39;s
                    browsing experience.
                  </p>
                  <p>
                    Session cookies are created temporarily and are used to link a user&#39;s actions during a specific browsing session. A
                    browsing session begins when a user opens a browser window and ends when they close it. Session cookies are stored in
                    the device&#39;s memory and are deleted as soon as the browser is closed. They are often used for tasks such as storing
                    items in a shopping cart or keeping a user logged in to a website.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'what-do-we-use-cookies-for',
              tabLabel: 'What do we use cookies for?',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>What do we use cookies for?</h3>
                  <p>This section refers to all categories of cookies and not just those we use on our website. </p>
                  <h4>1. Security</h4>
                  <p>
                    We refer to these as required cookies. They are used to authenticate users, prevent fraud and protect you as you
                    interact with a service. They enable services you have specifically asked for.
                  </p>
                  <p>
                    These cookies are essential to enable you to move around the website and use its features, such as accessing secure
                    areas of the website. Without these cookies certain services you have asked for cannot be provided.
                  </p>
                  <h4>2. Functionality</h4>
                  <p>
                    These cookies remember choices you make to improve your experience, such as your choice of language, and provide
                    enhanced, more personal features. They may also be used to help provide services you have asked for such as watching a
                    video or commenting on a blog. The information these cookies collect may be anonymized and they cannot track your
                    browsing activity to other websites.
                  </p>
                  <p>
                    These cookies allow us to enhance your browsing experience, tailor content to your preferences, and make your
                    interactions with our website more meaningful. For example, we may use functional cookies to determine whether you have
                    visited our website before and inform us which site features you are most interested in. Therefore, we can better
                    customize our website content to you to boost your experience.
                  </p>
                  <h4>3. Performance</h4>
                  <p>
                    These cookies are commonly known as website analytics cookies and are used to collect data on how users interact with a
                    website. The information collected includes details such as the most frequently visited pages, the time spent on each
                    page, and any error messages encountered during the browsing session. It&#39;s important to note that website analytics
                    cookies do not collect any personally identifiable information about the user. Instead, the data collected is aggregated
                    and remains anonymous.
                  </p>
                  <p>
                    The primary purpose of these cookies is to improve the performance and functionality of the website by providing
                    insights into user behavior.
                  </p>
                  <h4>4. Advertising or targeting</h4>
                  <p>
                    These cookies collect information about your browsing habits in order to make advertising relevant to you and your
                    interests. They are used to deliver ads more relevant to you and your interests. They are also used to limit the number
                    of times you see an advertisement as well as help measure the effectiveness of the advertising campaign. They are
                    usually placed by advertising networks with the website operator&#39;s permission. They remember that you have visited a
                    website and this information is shared with other organizations such as advertisers.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'which-cookies-do-you-use',
              tabLabel: 'Which cookies do you use?',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>Which cookies do you use?</h3>
                  <p>
                    This website uses first-party and some third-party cookies, both session-based and persistent cookies. We use the
                    categories of cookies below.
                  </p>
                  <h4>Functionality</h4>
                  <ul>
                    <li>
                      <p>__hssc from HubSpot: To store anonymized statistics about website sessions, duration 30 minutes </p>
                    </li>
                    <li>
                      <p>__cf_bm from CloudFlare: To read and filter requests from bad bots to protect the website, duration 30 minutes </p>
                    </li>
                    <li>
                      <p>gaspardesk from Gaspar AI: Used to store your cookies selections</p>
                    </li>
                  </ul>
                  <h4>Performance (Analytics)</h4>
                  <ul>
                    <li>
                      <p>__hstc from HubSpot: To store time of visit, duration 6 months </p>
                    </li>
                    <li>
                      <p>__hssrc from HubSpot: To store a unique session ID, duration end of session </p>
                    </li>
                    <li>
                      <p>hubspotutk from HubSpot: To keep track of a visitor&#39;s identity, duration 6 months </p>
                    </li>
                    <li>
                      <p>_ga from Google Analytics: To identify unique users, duration 2 years </p>
                    </li>
                    <li>
                      <p>
                        __ gid from Google Analytics: To identify the user - registers a unique ID that is used to generate statistical data
                        on how the visitor uses the website, duration 24 hours
                      </p>
                    </li>
                    <li>
                      <p>
                        <em>gat_gtag_UA</em> from Google Analytics: To store a unique user id and used to measure how users interact with
                        our website, duration 1 minute
                      </p>
                    </li>
                  </ul>
                </div>
              ),
            },
            {
              tabId: 'how-can-i-control-cookies',
              tabLabel: 'How can I control cookies?',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>How can I control cookies?</h3>
                  <p>You have the right to decide if you want to accept or reject cookies through the following methods: </p>
                  <p>
                    Cookies Settings: Here you can opt in or out of cookies and choose which ones are placed on our website <br />
                    Browser Controls: You can accept or reject cookies through changing your web browser settings. If you want to reject
                    cookies through your browser, you should visit your browser help menu for further information. In the scenario where you
                    reject cookies, you may still be able to use our website, but some parts may be restricted.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'do-not-track-instructions',
              tabLabel: 'Do Not Track instructions',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>Do Not Track instructions</h3>
                  <p>
                    Gaspar AI tries to respect the &quot;do not track&quot; (DNT) instructions that we receive from a user&#39;s browser.
                    However, due to the absence of a universal industry standard for DNT technology, we cannot guarantee that Gaspar AI will
                    always respond to such signals.
                  </p>
                  <p>
                    We continue monitoring advancements in DNT technology and keeping abreast of DNT industry standards as they progress.
                    Our goal is to provide our users with the best possible online experience while respecting their privacy preferences.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'changes-to-this-cookie-policy',
              tabLabel: 'Changes to this cookie policy',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>Changes to this cookie policy</h3>
                  <p>
                    We review this cookie policy on a regular basis to ensure that it is up-to-date with our use of your personal
                    information, and compliant with applicable data protection laws.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'ip-address',
              tabLabel: 'IP Address',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>IP Address</h3>
                  <p>
                    An IP address is a distinct identifier that electronic devices use to communicate with one another on the Internet. When
                    you visit our website, we may capture the IP address of the device you use to connect to the Internet.
                  </p>
                  <p>
                    We use this information to determine the general physical location of the device and the geographic regions of our
                    visitors. This data helps us tailor our website content to better meet the needs of our audience and improve your
                    overall online experience.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'contact-information',
              tabLabel: 'Contact Information',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>Contact Information</h3>
                  <p>
                    To ask questions or comment about our information practices, please contact us
                    <Link target="_blank" rel="nofollow" href="https://www.gaspar.ai/contact-us">
                      here
                    </Link>
                    or send an email to
                    <Link target="_blank" rel="nofollow" href="mailto:support@gaspar.ai">
                      support@gaspar.ai
                    </Link>
                    .
                  </p>
                </div>
              ),
            },
            {
              tabId: 'control-your-cookies',
              tabLabel: 'Control your cookies',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>Control your cookies</h3>
                  <p>Here you can opt in or out of cookies and choose whihc ones are placed on our website</p>
                  <GasparCookiesProvider>
                    <ManageCookiesForm />
                  </GasparCookiesProvider>
                </div>
              ),
            },
          ]}
        />
      </PageSection>
    </>
  );
};
CookiesPolicyPage.displayName = 'CookiesPolicyPage';
export default CookiesPolicyPage;
